import {
  LayoutDashboard,
  Package,
  Warehouse,
  ArrowRightLeft,
  ClipboardCheck,
  Barcode,
  ShoppingCart,
  Truck,
  Users,
  TrendingUp,
  Package2,
  FolderTree,
  Factory,
  FileText,
  Wrench,
  BrainCircuit,
  Target,
  TrendingDown,
  PoundSterling,
  Grid3x3,
  BarChart3,
  DollarSign,
  Settings,
} from "lucide-react";
import { MenuItem } from "../types";
import { getCurrentFeatureFlags } from "@/config/feature-flags";

/**
 * Phase 1 Badge Configuration
 * Identifies which menu items are part of Phase 1 release
 */
export type PhaseStatus = "phase1" | "phase2" | "future";

export interface PhaseInfo {
  status: PhaseStatus;
  label?: string;
  tooltip?: string;
}

/**
 * Options for customizing menu items
 */
export interface MenuItemsOptions {
  /** Custom label for level 1 hierarchy (replaces "Locations" in Inventory and Settings) */
  level1Label?: string;
}

/**
 * Get menu items with feature flag filtering applied
 * This function dynamically filters menu items based on enabled features
 */
export const getMenuItems = (options: MenuItemsOptions = {}): MenuItem[] => {
  const flags = getCurrentFeatureFlags();
  const { level1Label = "Locations" } = options;

  const baseMenuItems: MenuItem[] = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, page: "dashboard", phase: "phase1" },
    {
      id: "inventory",
      label: "Inventory",
      icon: Package,
      page: "inventory",
      phase: "phase1",
      children: [
        { id: "stock-items", label: "Stock Items", icon: Package, page: "stock-items", phase: "phase1" },
        ...(flags.enableWarehouseManagement
          ? [{ id: "warehouses", label: level1Label, icon: Warehouse, page: "warehouses", phase: "phase1" }]
          : []),
        ...(flags.enableWarehouseManagement
          ? [
              {
                id: "stock-transfers",
                label: "Stock Transfers",
                icon: ArrowRightLeft,
                page: "stock-transfers",
                phase: "phase1",
              },
            ]
          : []),
        { id: "add-movement", label: "Stock Movements", icon: ArrowRightLeft, page: "add-movement", phase: "phase1" },
        {
          id: "stock-movements",
          label: "Stock Movement History",
          icon: FileText,
          page: "stock-movements",
          phase: "phase1",
        },
        { id: "stock-takes", label: "Stock Takes", icon: ClipboardCheck, page: "stock-takes", phase: "phase1" },
        { id: "tracking", label: "Lot/Batch/Serial Tracking", icon: Barcode, page: "tracking" },
      ],
    },
    {
      id: "purchasing",
      label: "Purchasing",
      icon: ShoppingCart,
      page: "purchasing",
      phase: "phase1",
      children: [
        { id: "purchase-orders", label: "Purchase Orders", icon: FileText, page: "purchase-orders", phase: "phase1" },
        { id: "receive-goods", label: "Receive Goods", icon: Package2, page: "receive-goods", phase: "phase1" },
        { id: "suppliers", label: "Suppliers", icon: Truck, page: "suppliers", phase: "phase1" },
      ],
    },
    {
      id: "sales",
      label: "Sales & Fulfillment",
      icon: TrendingUp,
      page: "sales",
      phase: "phase1",
      children: [
        { id: "sales-orders", label: "Sales Orders", icon: FileText, page: "sales-orders", phase: "phase1" },
        { id: "pick-pack", label: "Pick & Pack", icon: Package2, page: "pick-pack" },
        { id: "shipping", label: "Shipping", icon: Truck, page: "shipping" },
        { id: "customers", label: "Customers", icon: Users, page: "customers", phase: "phase1" },
      ],
    },
    {
      id: "master-data",
      label: "Master Data",
      icon: FolderTree,
      page: "master-data",
      phase: "phase1",
      children: [
        {
          id: "projects-cost-centers",
          label: "Projects & Cost Centers",
          icon: FolderTree,
          page: "projects-cost-centers",
        },
      ],
    },
    {
      id: "manufacturing",
      label: "Manufacturing",
      icon: Factory,
      page: "manufacturing",
      phase: "future",
      children: [
        { id: "boms", label: "Bills of Material (BOMs)", icon: FileText, page: "boms", phase: "future" },
        { id: "work-orders", label: "Work Orders", icon: Wrench, page: "work-orders", phase: "future" },
        {
          id: "production-schedule",
          label: "Production Schedule",
          icon: BarChart3,
          page: "production-schedule",
          phase: "future",
        },
        { id: "routing", label: "Routings & Work Centers", icon: Factory, page: "routing", phase: "future" },
      ],
    },
    {
      id: "ai",
      label: "AI Forecasting & Analytics",
      icon: BrainCircuit,
      page: "ai",
      phase: "phase1",
      children: [
        { id: "ai-forecasting", label: "Demand Forecasting", icon: TrendingUp, page: "ai-forecasting" },
        { id: "abc-classification", label: "ABC Classification", icon: Target, page: "abc-classification" },
        { id: "reorder", label: "Reorder Recommendations", icon: TrendingDown, page: "reorder", phase: "future" },
      ],
    },
    {
      id: "pricing",
      label: "Pricing",
      icon: PoundSterling,
      page: "pricing",
      phase: "future",
      children: [
        { id: "price-matrix", label: "Price Matrix", icon: Grid3x3, page: "price-matrix", phase: "future" },
        { id: "margin-analysis", label: "Margin Analysis", icon: BarChart3, page: "margin-analysis", phase: "future" },
        { id: "cost-analysis", label: "Cost Analysis", icon: DollarSign, page: "cost-analysis", phase: "future" },
      ],
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      page: "settings",
      phase: "phase1",
      children: [
        { id: "stock-options", label: "System Configuration", icon: Settings, page: "stock-options", phase: "phase1" },
        {
          id: "warehouse-location-setup",
          label: level1Label,
          icon: Warehouse,
          page: "warehouse-location-setup",
          phase: "phase1",
        },
        {
          id: "integration-sync",
          label: "Integration & Sync",
          icon: ArrowRightLeft,
          page: "integration-sync",
          phase: "phase1",
        },
        {
          id: "company-settings",
          label: "Company Settings",
          icon: FileText,
          page: "company-settings",
          phase: "phase1",
        },
        { id: "user-management", label: "User Management", icon: Users, page: "user-management", phase: "phase1" },
        { id: "custom-fields", label: "Custom Fields", icon: Grid3x3, page: "custom-fields", phase: "phase1" },
        { id: "fifo-demo", label: "FIFO Demo", icon: Package, page: "fifo-demo" },
        { id: "permissions-demo", label: "Permissions Demo", icon: Users, page: "permissions-demo" },
        { id: "help-system-demo", label: "Help System Demo", icon: FileText, page: "help-system-demo" },
      ],
    },
  ];

  return baseMenuItems;
};

/**
 * Backward compatible export - gets menu items with feature flags applied
 */
export const menuItems = getMenuItems();

/**
 * Company Options Configuration (Database/Environment Selector)
 */
export const companies = [
  { value: "production", label: "Acme Corporation Ltd" },
  { value: "staging", label: "Beta Industries Ltd" },
  { value: "test", label: "Gamma Trading Co" },
];

/**
 * Get menu configuration with dynamic page count
 */
export const getMenuConfig = () => {
  const items = getMenuItems();
  return {
    /** Total number of pages - calculated from menuItems */
    totalPages: items.reduce((total, item) => {
      return total + 1 + (item.children?.length || 0);
    }, 0),
    /** Minimum pages required to enable favorites feature */
    favoritesThreshold: 10,
    /** Show company switcher in footer */
    showDatabaseSwitcher: true,
    /** Show settings button in footer - settings is already in main menu */
    showSettings: false,
  };
};

/**
 * Menu Configuration - backward compatible export
 */
export const menuConfig = getMenuConfig();
